/* Add-to-cart helper for the wishlist (and any Bokja "quick add"), hooked into Horizon's native cart
   refresh. An ES module so it can import Horizon's real CartAddEvent + fetchConfig (loaded via the
   importmap in scripts.liquid); it exposes window.BokjaCartAdd for the plain wishlist script to call.

   Flow mirrors product-form.js: POST id+quantity+sections to cart_add_url, then dispatch a bubbling
   CartAddEvent so the cart drawer / icon re-render themselves from response.sections. The cart drawer
   morph that follows re-renders our tab bar back to the Cart tab, so the shopper lands on the updated
   cart after adding from the wishlist. */
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

async function addVariant(variantId, sourceEl) {
  if (!variantId) return { ok: false };

  // Same section-ids product-form.js asks for, so Horizon returns the rendered cart drawer to morph.
  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
  });

  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', '1');
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  const cfg = fetchConfig('javascript', { body });

  let data;
  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'text/html' },
    });
    data = await res.json();
  } catch (e) {
    return { ok: false };
  }

  const target = sourceEl && sourceEl.isConnected ? sourceEl : document.body;

  if (data && data.status) {
    target.dispatchEvent(new CartAddEvent({}, 'bokja-wishlist', { didError: true, source: 'bokja-wishlist' }));
    return { ok: false, message: data.message };
  }

  target.dispatchEvent(
    new CartAddEvent(data, 'bokja-wishlist', {
      source: 'bokja-wishlist',
      itemCount: 1,
      productId: data && data.product_id,
      sections: data && data.sections,
    })
  );
  return { ok: true };
}

window.BokjaCartAdd = addVariant;

// 2026-09-18 (James, "Add all" froze the drawer): several variants in ONE cart call and ONE drawer
// morph, the same event the single add dispatches, so the cart re-renders once instead of N times.
async function addVariants(variantIds, sourceEl) {
  const ids = (variantIds || []).map((v) => Number(v)).filter(Boolean);
  if (!ids.length) return { ok: false };
  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
  });
  let data;
  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })), sections: sectionIds.join(',') }),
    });
    data = await res.json();
  } catch (e) {
    return { ok: false };
  }
  const target = sourceEl && sourceEl.isConnected ? sourceEl : document.body;
  if (data && data.status) {
    target.dispatchEvent(new CartAddEvent({}, 'bokja-wishlist', { didError: true, source: 'bokja-wishlist' }));
    return { ok: false, message: data.message };
  }
  const first = (data && data.items && data.items[0]) || {};
  target.dispatchEvent(
    new CartAddEvent(first, 'bokja-wishlist', {
      source: 'bokja-wishlist',
      itemCount: ids.length,
      productId: first.product_id,
      sections: data && data.sections,
    })
  );
  return { ok: true };
}
window.BokjaCartAddMany = addVariants;

// Generic quick-add: any element with [data-bokja-quickadd] + data-variant-id adds to cart (used by
// the recently-viewed strip and the collection-card "+"). preventDefault stops the surrounding link.
document.addEventListener('click', (e) => {
  const btn = e.target.closest && e.target.closest('[data-bokja-quickadd]');
  if (!btn) return;
  e.preventDefault();
  const variant = btn.getAttribute('data-variant-id');
  if (!variant || btn.classList.contains('is-adding')) return;
  btn.classList.add('is-adding');
  addVariant(variant, btn).finally(() => btn.classList.remove('is-adding'));
});

document.dispatchEvent(new Event('bokja:cartadd:ready'));
